import {
  AuthenticationStatus,
  RainbowKitAuthenticationProvider,
  RainbowKitProvider,
  connectorsForWallets,
  createAuthenticationAdapter,
  darkTheme,
  getDefaultConfig,
  lightTheme,
} from '@rainbow-me/rainbowkit';
import '@rainbow-me/rainbowkit/styles.css';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { SiweMessage } from 'siwe';
import { SignerProvider } from 'src/contexts/SignerContext';
import { SocketProvider } from 'src/contexts/SocketContext';
import { createContext } from 'src/helpers/context';
import {
  accessTokenIAO,
  desktopAddressKey,
  referralCodeIAO,
  removeAuthTokens,
} from 'src/helpers/storage';
import { returnRawValue, useLocalStorage } from 'src/hooks/useLocalStorage';
import { THEME_MODE } from 'src/interfaces/theme';
import { LoginResponse, useLogin } from 'src/services/auth/login';
import { useVerifyToken } from 'src/services/auth/verify';
import { useAppDispatch, useAppSelector } from 'src/store';
import { Chain } from 'viem/chains';
import {
  CreateConnectorFn,
  ResolvedRegister,
  WagmiProvider,
  createConfig,
  createStorage,
  http,
  useAccount,
  useChainId,
  useDisconnect,
} from 'wagmi';
import {
  arbitrumMainnet,
  arbitrumTestnet,
  bitLayerMainnet,
  bitLayerTestnet,
  nibiruMainnet,
  nibiruTestnet,
  sei,
  seiDevnet,
  bscMainnet,
  bscTestnet,
  sepoliaConfig,
  ethereumMainnet,
  avalancheMainnet,
  avalancheTestnet,
} from './network-config';
import { queryClient } from './react-query';
import {
  metaMaskWallet,
  rainbowWallet,
  walletConnectWallet,
  trustWallet,
  coin98Wallet,
  coinbaseWallet,
  rabbyWallet,
  safeWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { ERRORS } from '../constants/exchange';
import { AccessCodeModal } from '../components/01.layout/header/AccessCodeModal';
import { useDisclosure } from '../hooks/useDisclosure';
import { GetReferralCodeModal } from '../components/01.layout/header/GetReferralCodeModal';
import { Modal } from 'antd';
import useWalletButtonsInterceptor from '../hooks/useWalletButtonsInterceptor';
import { PublicSocketProvider } from '../contexts/PublicSocketContext';
import { clearUserInfo, setUserInfo } from '../store/actions/user';
import { userKeys } from '../services/user/user-keys';
import { AuthenticationAdapter } from '@rainbow-me/rainbowkit/dist/components/RainbowKitProvider/AuthenticationContext';

const useTestnet = process.env.REACT_APP_TESTNET === 'true';
const featureToggle = process.env.REACT_APP_ENABLE_TEST_FEATURE_TOGGLE === 'true';

const testEnabledUsers = (process.env.REACT_APP_TEST_ADDRESSES ?? '')
  .split(',')
  .map((addr) => addr.toLowerCase());
const enabledTestChains = (process.env.REACT_APP_TEST_CHAINS ?? '')
  .split(',')
  .map((name) => name.toLowerCase());

function isChainEnabled(chainName: string, userAddress: string | undefined): boolean {
  if (!featureToggle || !userAddress) return true;

  if (!enabledTestChains.includes(chainName.toLowerCase())) return true;

  return (
    enabledTestChains.includes(chainName.toLowerCase()) &&
    testEnabledUsers.includes(userAddress.toLowerCase())
  );
}

export const getChains = (useTestnet: boolean): Partial<Record<any, any>> => {
  const chains: any = {};

  const allChains = {
    bitLayer: { mainnet: bitLayerMainnet, testnet: bitLayerTestnet },
    ethereum: {
      mainnet: ethereumMainnet,
      testnet: sepoliaConfig,
    },
    nibiru: { mainnet: nibiruMainnet, testnet: nibiruTestnet },
    arbitrum: { mainnet: arbitrumMainnet, testnet: arbitrumTestnet },
    binance: { mainnet: bscMainnet, testnet: bscTestnet },
    avalanche: { mainnet: avalancheMainnet, testnet: avalancheTestnet },
  };

  for (const [chainName, config] of Object.entries(allChains)) {
    const lowerName = chainName.toLowerCase();
    const userAddress = localStorage.getItem('userAddress');
    const isEnabled = isChainEnabled(lowerName, userAddress ?? undefined);
    if (!isEnabled) continue;

    if (!useTestnet) {
      chains[chainName] = config.mainnet;
      continue;
    }

    chains[chainName] = config.testnet;
  }

  return chains;
};
export const chains = getChains(useTestnet);

export const getChainId = (chainName: string) => {
  return chains[chainName]?.id ?? 0;
};

const connectors = connectorsForWallets(
  [
    {
      groupName: 'Wallets',
      wallets: [
        metaMaskWallet,
        rainbowWallet,
        trustWallet,
        coin98Wallet,
        coinbaseWallet,
        walletConnectWallet,
        safeWallet,
        rabbyWallet,
      ],
    },
  ],
  {
    appName: 'Vdex',
    projectId: '18b664217a500896de2d3b2e396ccab3',
  },
) as CreateConnectorFn[];

const chainList = Object.values(chains);

export const wagmiChains = chainList as unknown as readonly [Chain, ...Chain[]];

export const wagmiConfig: ResolvedRegister['config'] = createConfig({
  connectors,
  chains: wagmiChains,
  transports: {
    1: http(), // Ethereum Mainnet
    200810: http(), // BitLayer Testnet
    11155111: http(), // Sepolia
    713715: http(), // Sei Testnet
    200901: http(), // BitLayer Mainnet
    1329: http(), // Sei Devnet
    6911: http(), // Nibiru Testnet
    6900: http(), // Nibiru Mainnet
    42161: http(), // Arbitrum Mainnet
    421614: http(), // Arbitrum Sepolia
    56: http(), // BSC Mainnet
    97: http(), // BSC Testnet
    43113: http(), // Avalanche Testnet
    43114: http(), // Avalanche Mainnet
  },
});

export const config = getDefaultConfig({
  appName: 'dex',
  projectId: '85e9ebb34585fc3bde5e0ed8cb752d6b',
  chains: Object.values(chains) as any,
  pollingInterval: 5000,
});

const [AuthStatusProvider, useAuthStatus] = createContext<{
  authStatus: AuthenticationStatus;
  setAuthStatus: (val: AuthenticationStatus) => void;
}>({
  hookName: 'useAuthStatus',
  providerName: 'AuthStatusProvider',
});

export const SiweProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [accessToken] = useLocalStorage(accessTokenIAO, undefined, {
    serialize: returnRawValue,
    deserialize: returnRawValue,
  });
  const [openAccessCodeModal, { open: openAccessModal, close: closeAccessModal }] = useDisclosure();
  const [authenticationStatus, setAuthenticationStatus] = useState<AuthenticationStatus>('loading');
  const { disconnect } = useDisconnect();

  const [
    isOpenGetReferralCodeModal,
    { open: openGetReferralCodeModal, close: closeGetReferralCodeModal },
  ] = useDisclosure();

  const verifyTokenQuery = useVerifyToken();
  const loginMutation = useLogin();
  const chainId = useChainId();
  const searchParams = new URLSearchParams(window.location.search);
  let referralCode = searchParams.get('referral');
  if (!referralCode) {
    referralCode = localStorage.getItem(referralCodeIAO);
  }
  const { address: userAddress } = useAccount();
  if (userAddress) {
    localStorage.setItem('userAddress', userAddress);
  }

  const authenticationAdapter: AuthenticationAdapter<SiweMessage> = useMemo(
    () =>
      createAuthenticationAdapter({
        getNonce: async () => {
          return 'nonce';
        },

        createMessage: ({ address, chainId }) => {
          return new SiweMessage({
            domain: window.location.host,
            address,
            statement: 'Sign in with Ethereum to the app.',
            uri: window.location.origin,
            version: '1',
            chainId,
          });
        },

        getMessageBody: ({ message }) => {
          return message.prepareMessage();
        },

        verify: async ({ message, signature }) => {
          try {
            await loginMutation
              .mutateAsync({
                address: userAddress!,
                signature,
                message: message.prepareMessage(),
                referrerCode: referralCode ?? userAddress,
                chainId: chainId,
              })
              .then((data: LoginResponse) => {
                if (data.isNewUser) {
                  Modal.info({
                    icon: <span className="text-[22px] mr-[4px] translate-y-[-8px]">🥂</span>,
                    title: 'Welcome to VDEX!',
                    okText: 'Let’s GO!',
                    content: (
                      <>
                        <p>
                          Congratulations on becoming one of the few traders to get exclusive access
                          to VDEX in beta!
                        </p>
                        <br />
                        <p>
                          Since we are in beta, you might encounter some bugs or discrepancies. We
                          encourage you join{' '}
                          <a
                            href="https://t.me/+aJ2CtyNEb89mYjkx"
                            target="_blank"
                            className="text-[#1890ff]"
                            rel="noopener noreferrer"
                          >
                            Telegram
                          </a>{' '}
                          and report it for a chance to win exclusive badges and up to $5,000 in
                          bounties.
                        </p>
                      </>
                    ),
                  });
                }
                dispatch(
                  setUserInfo({
                    walletAddress: userAddress,
                    currentChain: chainId,
                  }),
                );
              });

            return true;
          } catch (error: any) {
            console.log('🚀 ~ verify: ~ error:', error);
            if (error?.response?.data?.code === ERRORS.INVALID_OR_ALREADY_USED_REFERRAL_CODE.code) {
              openAccessModal();
            }

            return false;
          }
        },

        signOut: async () => {
          console.log('disconnected wallet'); // this log for debugging
        },
      }),
    [loginMutation, userAddress, accessToken],
  );

  useEffect(() => {
    if (verifyTokenQuery.isError) {
      removeAuthTokens();
      dispatch(clearUserInfo());
      try {
        disconnect();
      } catch {}
      queryClient.resetQueries({
        queryKey: userKeys.all,
      });
    }
  }, [verifyTokenQuery.isError]);

  useEffect(() => {
    if (verifyTokenQuery.isSuccess) {
      setAuthenticationStatus('authenticated');
    } else if (verifyTokenQuery.isFetching) {
      setAuthenticationStatus('loading');
    } else {
      setAuthenticationStatus('unauthenticated');
    }
  }, [verifyTokenQuery]);

  return (
    <RainbowKitAuthenticationProvider adapter={authenticationAdapter} status={authenticationStatus}>
      <AuthStatusProvider
        value={{
          authStatus: authenticationStatus,
          setAuthStatus: setAuthenticationStatus,
        }}
      >
        {children}
      </AuthStatusProvider>
      <AccessCodeModal
        open={openAccessCodeModal}
        onClose={closeAccessModal}
        onOk={openGetReferralCodeModal}
      />
      <GetReferralCodeModal open={isOpenGetReferralCodeModal} onClose={closeGetReferralCodeModal} />
    </RainbowKitAuthenticationProvider>
  );
};

export const useAuth = () => {
  const { authStatus, setAuthStatus } = useAuthStatus();
  useWalletButtonsInterceptor();

  return {
    status: authStatus,
    isAuthenticated: authStatus === 'authenticated',
    isUnauthenticated: authStatus === 'unauthenticated',
    isPending: authStatus === 'loading',
    setAuthStatus: setAuthStatus,
    isLinkedDesktop: !!localStorage.getItem(desktopAddressKey),
  };
};

const rkDarkTheme = darkTheme();
const rkLightTheme = lightTheme();

export const RainbowProvider = ({ children }: { children: React.ReactNode }) => {
  const theme = useAppSelector((state) => state.theme);
  const rkTheme = theme === THEME_MODE.DARK ? rkDarkTheme : rkLightTheme;

  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <SiweProvider>
          <RainbowKitProvider
            initialChain={chains.ethereum.id}
            theme={{ ...rkTheme, fonts: { body: 'Poppins' } }}
          >
            <SignerProvider>
              <PublicSocketProvider>
                <SocketProvider>{children}</SocketProvider>
              </PublicSocketProvider>
            </SignerProvider>
          </RainbowKitProvider>
        </SiweProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};
